import { ProcessStepCatalogDto } from "#modules/quoteRequestLines/infra/fetchProcessStepCatalog.api";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { QuoteRequestLineFormData } from "#modules/quoteRequestLines/types/QuoteRequestLineFormData";
import {
  areDescriptionsEqual,
  createKeyFromDescription,
  CustomInput,
  getQuoteRequestLineProductLines,
  IndustrialProcessType,
  isOutsourcingProcessStep,
  isProductProcessStep,
  isSurfaceTreatmentProcessStep,
  PartTypeSchema,
} from "shared";

import { NO_SURFACE_TREATMENT_KEY } from "./views/PredictionData/SurfaceTreatmentSelect";
import { CustomInputsDto } from "./views/useCustomInputs";

export const isAddedValueProcessStep = (processStep: {
  industrialProcess: IndustrialProcessType;
  isOutsourced?: boolean | null;
}) => {
  return !isProductProcessStep(processStep);
};

export const getDefaultValues = ({
  customInputs,
  customInputsValues,
  quoteRequestLine,
  surfaceTreatments,
}: {
  quoteRequestLine: QuoteRequestLineDto | undefined | null;
  customInputsValues: CustomInput[];
  customInputs: CustomInputsDto;
  surfaceTreatments: ProcessStepCatalogDto;
}): QuoteRequestLineFormData => {
  const currentMaterialLayout = quoteRequestLine?.layout?.materialLayout;

  const persistedQuantity = quoteRequestLine?.quantity;

  const persistedMaterialGrade = quoteRequestLine?.materialGrade ?? null;

  const productLines = getQuoteRequestLineProductLines(quoteRequestLine ?? { processSteps: [] });

  const sortedCustomInputsValues =
    customInputsValues.length > 0
      ? customInputs
          .map((customInput) => {
            const customInputValue = customInputsValues.find((ci) => ci.key === customInput.key);

            if (!customInputValue) {
              return {
                id: undefined,
                index: customInput.index,
                key: customInput.key,
                label: customInput.label,
                value: customInput.defaultValue || null,
              };
            }

            return {
              ...customInputValue,
              index: customInput.index,
            };
          })
          .sort((a, b) => a.index - b.index)
      : customInputs
          .sort((a, b) => a.index - b.index)
          .map((customInput) => ({
            id: undefined,
            key: customInput.key,
            label: customInput.label,
            value: customInput.defaultValue || null,
          }));

  const surfaceTreatmentProcessStep = quoteRequestLine?.processSteps.find(
    isSurfaceTreatmentProcessStep,
  );

  const currentSurfaceTreatment = surfaceTreatments.find((surfaceTreatment) =>
    areDescriptionsEqual(surfaceTreatment.description, surfaceTreatmentProcessStep?.description),
  );

  const defaultValues: QuoteRequestLineFormData = {
    quoteRequestLineId: quoteRequestLine?.id,
    reworkRatio: quoteRequestLine?.reworkRatio ?? 0,
    isDetailsPanelDisplayable: false,
    quantity: persistedQuantity,
    consumablePrice: productLines[0]?.price ?? 0,
    cluster: [],
    designOfficeTime: quoteRequestLine?.designOfficeTime,
    designOfficeHourlyRate: quoteRequestLine?.designOfficeHourlyRate,
    designOfficeMarginRatio: quoteRequestLine?.designOfficeMarginRatio,
    materialGrade: persistedMaterialGrade,
    materialName: quoteRequestLine?.materialName ?? "",
    comment: quoteRequestLine?.comment ?? "",
    internalComment: quoteRequestLine?.internalComment ?? "",
    part: quoteRequestLine?.part,
    partId: quoteRequestLine?.partId,
    processSteps: quoteRequestLine?.processSteps.filter(isAddedValueProcessStep),
    outsourcingProcessSteps: quoteRequestLine?.processSteps.filter(isOutsourcingProcessStep),
    layout: quoteRequestLine?.layout
      ? {
          ...quoteRequestLine.layout,
          createdAt: new Date(quoteRequestLine.layout.createdAt),
          updatedAt: new Date(quoteRequestLine.layout.updatedAt),
          status: quoteRequestLine.layout.status,
          scrapMaterialBillingRate: quoteRequestLine.layout.scrapMaterialBillingRate,
          materialsLogisticsCostsRatio: quoteRequestLine.layout.materialsLogisticsCostsRatio,
          materialLayout: currentMaterialLayout
            ? {
                ...currentMaterialLayout,
                price: currentMaterialLayout.price ?? 0,
                quantity: currentMaterialLayout.quantity,
                maxPartsPerLayout: currentMaterialLayout.maxPartsPerLayout ?? null,
                type: currentMaterialLayout.type || PartTypeSchema.Enum.OTHER,
                pricingMode: currentMaterialLayout.pricingMode,
                volume: currentMaterialLayout.volume || null,
                volumetricPrice: currentMaterialLayout.volumetricPrice || null,
              }
            : null,
        }
      : null,
    productLines,
    customInputs: sortedCustomInputsValues,
    surfaceTreatmentKey: currentSurfaceTreatment
      ? createKeyFromDescription(currentSurfaceTreatment.description)
      : NO_SURFACE_TREATMENT_KEY,
  };

  return defaultValues;
};
